// Case studies. Each one shows the screenshots captured at handover
// (npm run capture -- <url> --slug <slug>), not the live site, because
// clients change their stores after we deliver them.
//
// delivered: 'YYYY-MM' handover date, shown as "Delivered <month year>".
// TODO: fill in real handover dates; null hides the date.
// capture: null until the project has been captured; the thumbnail is used instead.
//
// Only claim features the store really has. Confirmed: SEO on every site;
// an AI chat agent on Aura Home, Aurelin, Elena Cosmetics, Hold, Les Ashels
// and Adaan; a floating WhatsApp button on the rest; Aurelin's loyalty points.
//
// Arabic text is a first draft and should be reviewed by a native speaker.

const capture = (slug, { video = false } = {}) => ({
  desktop: `/case-studies/${slug}/desktop.jpg`,
  mobile: `/case-studies/${slug}/mobile.jpg`,
  // First-screen previews (npm run previews) for cards and still frames.
  previewDesktop: `/case-studies/${slug}/preview-desktop.webp`,
  previewMobile: `/case-studies/${slug}/preview-mobile.webp`,
  video: video ? `/case-studies/${slug}/scroll.webm` : null,
});

export const services = {
  shopify: { en: 'Shopify store', ar: 'متجر شوبيفاي' },
  identity: { en: 'Brand identity', ar: 'هوية بصرية' },
  packaging: { en: 'Packaging', ar: 'تصميم التغليف' },
};

// Building blocks shared by most stores.
const included = {
  seo: {
    en: 'SEO built in: optimised titles, meta descriptions, clean URLs, image alt text and heading structure, so the store gets found on Google',
    ar: 'تحسين محركات البحث من البداية: عناوين ووصف محسّنان وروابط نظيفة ونصوص بديلة للصور وتسلسل عناوين صحيح، ليظهر المتجر في نتائج جوجل',
  },
  aiChat: {
    en: 'AI chat agent trained only on this store’s products and policies, answering customers instantly, day and night',
    ar: 'وكيل محادثة بالذكاء الاصطناعي مدرَّب على منتجات هذا المتجر وسياساته فقط، يجيب العملاء فوراً ليلاً ونهاراً',
  },
  whatsapp: {
    en: 'Floating WhatsApp button, so customers can message the brand from any page in one tap',
    ar: 'زر واتساب عائم ليراسل العملاء العلامة من أي صفحة بلمسة واحدة',
  },
  tracking: {
    en: 'Google Analytics and Meta Pixel set up to measure every visit, add-to-cart and sale, ready for ads',
    ar: 'إعداد Google Analytics وMeta Pixel لقياس كل زيارة وإضافة للسلة وعملية بيع، وجاهز للإعلانات',
  },
  mobile: {
    en: 'Mobile-first design that loads fast and makes checkout effortless on any phone',
    ar: 'تصميم للجوال أولاً سريع التحميل يجعل الدفع سهلاً على أي هاتف',
  },
  dashboard: {
    en: 'Shopify dashboard set up for products, inventory and discounts, so the team runs the store without code',
    ar: 'لوحة تحكم شوبيفاي مجهّزة للمنتجات والمخزون والخصومات، ليدير الفريق المتجر دون برمجة',
  },
  reviews: {
    en: 'Product reviews that build trust and turn browsers into buyers',
    ar: 'تقييمات للمنتجات تبني الثقة وتحوّل الزوّار إلى مشترين',
  },
  bilingual: {
    en: 'Full Arabic and English storefront, each language laid out properly',
    ar: 'متجر كامل بالعربية والإنجليزية، مع تنسيق صحيح لكل لغة',
  },
  scalable: {
    en: 'Structure ready for upsells, new collections and growth',
    ar: 'بنية جاهزة للبيع الإضافي والمجموعات الجديدة والنمو',
  },
};

export const projects = [
  {
    slug: 'aura-home',
    name: 'Aura Home',
    url: 'aurahomeeg.com',
    flagship: true,
    delivered: null,
    services: ['identity', 'packaging', 'shopify'],
    industry: { en: 'Home and lifestyle', ar: 'المنزل وأسلوب الحياة' },
    thumbnail: '/images/aura-home-thumb.png',
    capture: capture('aura-home', { video: true }),
    summary: {
      en: 'A home brand built from nothing: name to logo, packaging, storefront and an AI-assisted Shopify store.',
      ar: 'علامة منزلية بُنيت من الصفر: من الاسم إلى الشعار والتغليف وواجهة المحل ومتجر شوبيفاي مدعوم بالذكاء الاصطناعي.',
    },
    problem: {
      en: 'Aura Home had products and an idea, but no name on a bag, no look and nowhere to sell online. Everything a customer would see had to be invented, and all of it had to feel like one brand.',
      ar: 'كان لدى Aura Home منتجات وفكرة، لكن بلا هوية ولا شكل ولا مكان للبيع على الإنترنت. كان لا بد من ابتكار كل ما سيراه العميل، وأن يبدو كله علامة واحدة متكاملة.',
    },
    approach: {
      en: 'We started with how the brand should feel, then designed the logo, colours, type and a pattern system drawn from the mark. That identity carried into bags, boxes, stickers and a storefront concept. Online, we built a custom, mobile-first Shopify store in the same green and sand, optimised it for search from day one, and added an AI chat agent trained on Aura Home’s own products so customers get answers the moment they ask.',
      ar: 'بدأنا بالإحساس الذي يجب أن تمنحه العلامة، ثم صمّمنا الشعار والألوان والخطوط ونظام نقوش مستوحى من الشعار. انتقلت هذه الهوية إلى الأكياس والعلب والملصقات وتصوّر لواجهة المحل. وعلى الإنترنت، بنينا متجر شوبيفاي مخصّصاً للجوال أولاً بالأخضر والرملي نفسيهما، وحسّنّاه لمحركات البحث من اليوم الأول، وأضفنا وكيل محادثة بالذكاء الاصطناعي مدرَّباً على منتجات Aura Home ليحصل العملاء على إجاباتهم فور سؤالهم.',
    },
    result: {
      en: 'One calm, consistent brand from the shop sign to the checkout page, easy to find on Google and ready to grow.',
      ar: 'علامة هادئة ومتّسقة من لافتة المحل حتى صفحة الدفع، يسهل إيجادها على جوجل وجاهزة للنمو.',
    },
    highlights: [
      { en: 'Logo, colour system, typography and visual direction', ar: 'الشعار ونظام الألوان والخطوط والاتجاه البصري' },
      { en: 'Custom pattern system and brand assets', ar: 'نظام نقوش خاص وأصول بصرية للعلامة' },
      { en: 'Shopping bags, shipping packaging and product boxes', ar: 'أكياس التسوّق وتغليف الشحن وعلب المنتجات' },
      { en: 'Storefront and in-store concept', ar: 'تصوّر واجهة المحل وتجربته من الداخل' },
      { en: 'Custom Shopify design and build', ar: 'تصميم وبناء متجر شوبيفاي مخصّص' },
      included.aiChat,
      included.seo,
      included.mobile,
      included.tracking,
      included.reviews,
    ],
  },
  {
    slug: 'aurelin',
    name: 'Aurelin',
    url: 'aurelineg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Jewellery', ar: 'مجوهرات' },
    thumbnail: '/images/aurelin-thumb.jpg',
    capture: capture('aurelin'),
    summary: {
      en: 'An elegant jewellery store with a loyalty programme that turns every order into points customers can cash in.',
      ar: 'متجر مجوهرات أنيق ببرنامج ولاء يحوّل كل طلب إلى نقاط يستبدلها العملاء بالمال.',
    },
    problem: {
      en: 'Aurelin makes modern, durable stainless steel jewellery for everyday wear. In a crowded market, the brand needed a store that feels premium, shows each piece clearly and gives customers a reason to come back.',
      ar: 'تصنع Aurelin مجوهرات عصرية متينة من الستانلس ستيل للاستخدام اليومي. وفي سوق مزدحم، احتاجت العلامة إلى متجر يبدو راقياً ويعرض كل قطعة بوضوح ويمنح العملاء سبباً للعودة.',
    },
    approach: {
      en: 'We designed a warm, elegant store that pairs lifestyle imagery with clear product shots, organised into collections and bundles that make browsing easy. We built in a loyalty programme where every purchase earns points that customers convert into money off later orders, plus an AI chat agent trained on Aurelin’s pieces, and optimised every page for search.',
      ar: 'صمّمنا متجراً دافئاً وأنيقاً يجمع بين صور أسلوب الحياة وصور المنتجات الواضحة، ومنظّماً في مجموعات وباقات تسهّل التصفّح. وأضفنا برنامج ولاء تمنح فيه كل عملية شراء نقاطاً يحوّلها العملاء إلى مال في طلباتهم القادمة، مع وكيل محادثة بالذكاء الاصطناعي مدرَّب على قطع Aurelin، وحسّنّا كل صفحة لمحركات البحث.',
    },
    result: {
      en: 'A store that looks as good as the jewellery and rewards customers for coming back.',
      ar: 'متجر بجمال المجوهرات نفسها، يكافئ العملاء على العودة.',
    },
    highlights: [
      {
        en: 'Loyalty programme: customers earn points on every purchase and convert them into money',
        ar: 'برنامج ولاء: يكسب العملاء نقاطاً مع كل عملية شراء ويحوّلونها إلى مال',
      },
      included.aiChat,
      { en: 'Collections and bundles that make discovery easy', ar: 'مجموعات وباقات تسهّل اكتشاف المنتجات' },
      included.seo,
      included.reviews,
      included.mobile,
      included.tracking,
      included.dashboard,
    ],
  },
  {
    slug: 'hold',
    name: 'Hold',
    url: 'holdeg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Women’s bags', ar: 'حقائب نسائية' },
    thumbnail: '/images/hold-thumb.jpg',
    capture: capture('hold'),
    summary: {
      en: 'A calm, editorial bag store where you shop by shape, see every colour at a glance and get answers from an AI assistant.',
      ar: 'متجر حقائب هادئ بطابع تحريري، تتسوّقين فيه حسب الشكل، وترين كل الألوان بنظرة، وتحصلين على إجابات من مساعد بالذكاء الاصطناعي.',
    },
    problem: {
      en: 'Hold sells totes, shoulder, hobo and crossbody bags in faux and vegan leather, most in several colours. Shoppers needed to find the right shape fast and see each bag’s options without digging.',
      ar: 'تبيع Hold حقائب توت وكتف وهوبو وكروس بودي من الجلد الصناعي والنباتي، ومعظمها بعدة ألوان. احتاج المتسوّقون إلى إيجاد الشكل المناسب بسرعة ورؤية خيارات كل حقيبة دون عناء.',
    },
    approach: {
      en: 'We built the store around a “Find your shape” entry point, put colour swatches on every product card, and wrote editorial sections that tell each bag’s story. It runs in English and Arabic, is optimised for search, and has an AI chat agent trained on Hold’s bags to answer questions about size, colour and delivery on the spot.',
      ar: 'بنينا المتجر حول مدخل «اختاري الشكل»، ووضعنا عيّنات الألوان على كل بطاقة منتج، وكتبنا أقساماً تحريرية تروي قصة كل حقيبة. يعمل المتجر بالإنجليزية والعربية، ومحسَّن لمحركات البحث، وفيه وكيل محادثة بالذكاء الاصطناعي مدرَّب على حقائب Hold ليجيب عن أسئلة المقاس واللون والتوصيل فوراً.',
    },
    result: {
      en: 'A store that feels as considered as the bags, where shape, colour and use are clear before the first tap.',
      ar: 'متجر بعناية الحقائب نفسها، يتّضح فيه الشكل واللون والاستخدام قبل أول لمسة.',
    },
    highlights: [
      { en: 'Shop by shape: totes, shoulder, hobo, crossbody and work bags', ar: 'التسوّق حسب الشكل: توت وكتف وهوبو وكروس بودي وحقائب العمل' },
      { en: 'Colour swatches on every product card', ar: 'عيّنات الألوان على كل بطاقة منتج' },
      { en: 'Editorial product stories on the homepage', ar: 'قصص تحريرية للمنتجات في الصفحة الرئيسية' },
      included.aiChat,
      included.bilingual,
      included.seo,
      included.reviews,
      { en: 'Newsletter sign-up to bring customers back', ar: 'اشتراك في النشرة البريدية لإعادة العملاء' },
      included.mobile,
    ],
  },
  {
    slug: 'kick',
    name: 'Kick',
    url: 'kickstore-9386.myshopify.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Men’s fashion', ar: 'أزياء رجالية' },
    thumbnail: '/images/kick-thumb.jpg',
    capture: capture('kick'),
    summary: {
      en: 'A bold menswear store where polos, tees, jeans, blazers and shoes are one tap from the homepage.',
      ar: 'متجر أزياء رجالية جريء، تصل فيه إلى البولو والتيشيرتات والجينز والبليزر والأحذية بلمسة واحدة من الصفحة الرئيسية.',
    },
    problem: {
      en: 'Kick sells a wide menswear range, from polo shirts to blazers and shoes. Shoppers needed to jump straight to the category they came for and see what’s new or on sale.',
      ar: 'تبيع Kick تشكيلة واسعة من الأزياء الرجالية، من قمصان البولو إلى البليزر والأحذية. احتاج المتسوّقون إلى الوصول مباشرة إلى الفئة التي جاؤوا من أجلها ورؤية الجديد والعروض.',
    },
    approach: {
      en: 'We built a homepage around category tiles, a sale campaign banner, a video feature and a product row for every category, so the whole range is visible from the first scroll. Every page is optimised for search, and a floating WhatsApp button lets customers ask about sizes and stock without leaving the store.',
      ar: 'بنينا صفحة رئيسية حول بطاقات الفئات ولافتة حملة التخفيضات وقسم فيديو وصف منتجات لكل فئة، لتظهر التشكيلة كاملة من أول تمرير. كل صفحة محسّنة لمحركات البحث، ويتيح زر واتساب العائم للعملاء السؤال عن المقاسات والمخزون دون مغادرة المتجر.',
    },
    result: {
      en: 'A store where every category, new arrival and offer is visible from the first scroll.',
      ar: 'متجر تظهر فيه كل فئة وكل منتج جديد وكل عرض من أول تمرير.',
    },
    highlights: [
      { en: 'Category tiles for polos, tees, jeans, blazers and shoes', ar: 'بطاقات فئات للبولو والتيشيرتات والجينز والبليزر والأحذية' },
      { en: 'Sale campaign banner and video feature', ar: 'لافتة حملة التخفيضات وقسم فيديو' },
      { en: 'Product rows per category, best sellers and new arrivals', ar: 'صفوف منتجات لكل فئة والأكثر مبيعاً والوصول الجديد' },
      included.whatsapp,
      included.seo,
      included.mobile,
      included.tracking,
      included.dashboard,
    ],
  },
  {
    slug: 'les-ashels',
    name: 'Les Ashels',
    url: 'lesashels.myshopify.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Women’s fashion', ar: 'أزياء نسائية' },
    thumbnail: '/images/les ashels.png',
    capture: capture('les-ashels'),
    summary: {
      en: 'A bilingual fashion store with fast filters, quick add-to-cart, a size guide and an AI assistant.',
      ar: 'متجر أزياء ثنائي اللغة بفلاتر سريعة وإضافة فورية للسلة ودليل مقاسات ومساعد بالذكاء الاصطناعي.',
    },
    problem: {
      en: 'Les Ashels sells to Arabic and English speakers, and shoppers needed to find the right piece and the right size quickly on their phones.',
      ar: 'تبيع Les Ashels لمتحدثي العربية والإنجليزية، وكان على المتسوّقين إيجاد القطعة والمقاس المناسبين بسرعة من هواتفهم.',
    },
    approach: {
      en: 'We designed a clean, feminine store in both languages and cut the steps to checkout: advanced filters, quick add-to-cart from the collection page and a size guide on every product. An Instagram feed and reviews add social proof, an AI chat agent trained on Les Ashels’ catalogue answers fit and delivery questions instantly, and every page is optimised for search.',
      ar: 'صمّمنا متجراً أنيقاً بلمسة أنثوية وبلغتين، وقلّلنا الخطوات حتى الدفع: فلاتر متقدّمة وإضافة سريعة للسلة من صفحة المجموعة ودليل مقاسات في كل منتج. ويضيف عرض إنستغرام والتقييمات دليلاً اجتماعياً، ويجيب وكيل محادثة بالذكاء الاصطناعي مدرَّب على منتجات Les Ashels عن أسئلة المقاس والتوصيل فوراً، وكل صفحة محسّنة لمحركات البحث.',
    },
    result: {
      en: 'Fewer steps from browsing to checkout, in whichever language the customer prefers.',
      ar: 'خطوات أقل من التصفّح إلى الدفع، باللغة التي يفضّلها العميل.',
    },
    highlights: [
      included.bilingual,
      { en: 'Advanced product filtering', ar: 'فلترة متقدّمة للمنتجات' },
      { en: 'Quick add-to-cart from collection pages', ar: 'إضافة سريعة إلى السلة من صفحات المجموعات' },
      { en: 'Size guide on every product', ar: 'دليل مقاسات في كل منتج' },
      included.aiChat,
      { en: 'Instagram feed and product reviews for social proof', ar: 'عرض إنستغرام وتقييمات المنتجات كدليل اجتماعي' },
      included.seo,
      included.tracking,
      included.mobile,
    ],
  },
  {
    slug: 'pp-luxline',
    name: 'PP Luxline',
    url: 'ppluxline.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Men’s fashion', ar: 'أزياء رجالية' },
    thumbnail: '/images/ppluxline.jpg',
    capture: capture('pp-luxline'),
    summary: {
      en: 'A sharp, premium menswear store that sells online and sends shoppers to the physical shop.',
      ar: 'متجر أزياء رجالية راقٍ يبيع على الإنترنت ويوجّه المتسوّقين إلى المحل الفعلي.',
    },
    problem: {
      en: 'PP Luxline needed its online store to feel as premium as its clothes, and to bring people into its physical store as well.',
      ar: 'احتاجت PP Luxline أن يبدو متجرها الإلكتروني راقياً كملابسها، وأن يجذب الناس إلى محلها الفعلي أيضاً.',
    },
    approach: {
      en: 'We designed a custom store around the product photography, with a clean, confident layout that keeps the clothes in front. An embedded Google Map with one-tap directions turns online visitors into walk-ins, a floating WhatsApp button keeps the conversation going, and every page is optimised for search.',
      ar: 'صمّمنا متجراً مخصّصاً حول صور المنتجات، بتصميم نظيف وواثق يبقي الملابس في الواجهة. وتحوّل خريطة جوجل المدمجة مع الاتجاهات بلمسة واحدة الزوّار إلى عملاء في المحل، ويُبقي زر واتساب العائم التواصل مستمراً، وكل صفحة محسّنة لمحركات البحث.',
    },
    result: {
      en: 'A store that sells online and points customers to the shop.',
      ar: 'متجر يبيع على الإنترنت ويرشد العملاء إلى المحل.',
    },
    highlights: [
      { en: 'Custom design tailored to the brand', ar: 'تصميم مخصّص يناسب العلامة' },
      { en: 'Product-first, premium layout', ar: 'تصميم راقٍ يضع المنتج أولاً' },
      { en: 'Google Maps store location with one-tap directions', ar: 'موقع المحل على خرائط جوجل مع الاتجاهات بلمسة واحدة' },
      included.whatsapp,
      included.seo,
      included.tracking,
      included.mobile,
      included.scalable,
    ],
  },
  {
    slug: 'fimka',
    name: 'Fimka',
    url: 'fimkaeg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Women’s fashion', ar: 'أزياء نسائية' },
    thumbnail: '/images/fimka-thumb.png',
    capture: capture('fimka'),
    summary: {
      en: 'An elegant, effortless store that lets feminine silhouettes do the talking.',
      ar: 'متجر أنيق وسلس يترك القصّات الأنثوية تتحدث عن نفسها.',
    },
    problem: {
      en: 'Fimka’s clothes are about clean cuts and ease. The store needed to feel the same: elegant, smooth and simple to shop.',
      ar: 'تتميّز ملابس Fimka بالقصّات النظيفة والراحة، وكان على المتجر أن يمنح الإحساس نفسه: أناقة وسلاسة وسهولة في التسوّق.',
    },
    approach: {
      en: 'We designed a clean, feminine interface that steps back so the products lead, with tidy collections for quick discovery and reviews that build trust. Every page is optimised for search, and a floating WhatsApp button puts the brand one tap away whenever a customer has a question.',
      ar: 'صمّمنا واجهة نظيفة بلمسة أنثوية تترك المساحة للمنتجات، مع مجموعات مرتّبة لاكتشاف سريع وتقييمات تبني الثقة. كل صفحة محسّنة لمحركات البحث، ويضع زر واتساب العائم العلامة على بُعد لمسة كلما كان لدى العميلة سؤال.',
    },
    result: {
      en: 'A premium shopping experience that matches the brand.',
      ar: 'تجربة تسوّق راقية تليق بالعلامة.',
    },
    highlights: [
      { en: 'Feminine UI aligned with the brand', ar: 'واجهة أنثوية متّسقة مع العلامة' },
      { en: 'Organised collections for easy discovery', ar: 'مجموعات منظّمة لاكتشاف سهل' },
      included.whatsapp,
      included.reviews,
      included.seo,
      included.tracking,
      included.mobile,
      included.scalable,
    ],
    testimonial: {
      name: 'Ibraam',
      quote: {
        en: 'ShopFlex Studios truly understood the essence of Fimka from day one. They translated our vision into a modern, feminine website that feels elegant, smooth, and effortless to shop.',
        ar: 'فهم فريق ShopFlex جوهر Fimka منذ اليوم الأول، وحوّل رؤيتنا إلى موقع عصري بلمسة أنثوية، أنيق وسلس وسهل التسوّق.',
      },
    },
  },
  {
    slug: 'q-san-luxury',
    name: 'Q-San Luxury',
    url: 'qsanluxury.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Wellness and home fragrance', ar: 'العناية والعطور المنزلية' },
    thumbnail: '/images/Q-San-logo.png',
    capture: null,
    summary: {
      en: 'A spa-calm store with how-to videos for every product.',
      ar: 'متجر بهدوء المنتجعات مع فيديوهات استخدام لكل منتج.',
    },
    problem: {
      en: 'Q-San’s massage tools, candles and diffusers need a little explaining, and the store had to feel as calm as the products.',
      ar: 'تحتاج أدوات التدليك والشموع والمعطّرات من Q-San إلى بعض الشرح، وكان على المتجر أن يكون هادئاً كمنتجاته.',
    },
    approach: {
      en: 'We designed a minimal, spa-inspired store with dedicated collections for candles, diffusers and wellness tools, and a custom “How to use” page with step-by-step videos for each product. A newsletter brings customers back, a floating WhatsApp button answers questions in one tap, and every page is optimised for search.',
      ar: 'صمّمنا متجراً بسيطاً مستوحى من المنتجعات بمجموعات مخصّصة للشموع والمعطّرات وأدوات العناية، وصفحة «طريقة الاستخدام» بفيديوهات خطوة بخطوة لكل منتج. وتعيد النشرة البريدية العملاء، ويجيب زر واتساب العائم عن الأسئلة بلمسة واحدة، وكل صفحة محسّنة لمحركات البحث.',
    },
    result: {
      en: 'Customers understand the products before they buy, in a store that feels like the brand.',
      ar: 'يفهم العملاء المنتجات قبل الشراء، في متجر يشبه العلامة.',
    },
    highlights: [
      { en: 'Custom “How to use” page with step-by-step videos', ar: 'صفحة «طريقة الاستخدام» بفيديوهات خطوة بخطوة' },
      { en: 'Collections for candles, diffusers and wellness tools', ar: 'مجموعات للشموع والمعطّرات وأدوات العناية' },
      { en: 'Newsletter for repeat customers', ar: 'نشرة بريدية للعملاء الدائمين' },
      included.whatsapp,
      included.reviews,
      included.seo,
      included.tracking,
      included.mobile,
    ],
    testimonial: {
      name: 'Alaa',
      quote: {
        en: 'The site feels calm, clean, and elegant, exactly the vibe we wanted for Q-San Luxury. Customers love how easy it is to browse and shop.',
        ar: 'الموقع هادئ ونظيف وأنيق، وهو بالضبط الإحساس الذي أردناه لـ Q-San Luxury. العملاء يحبّون سهولة التصفّح والتسوّق.',
      },
    },
  },
  {
    slug: 'elena-cosmetics',
    name: 'Elena Cosmetics',
    url: 'elenacosmeticseg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Beauty', ar: 'مستحضرات التجميل' },
    thumbnail: '/images/elena-thumb.jpg',
    capture: capture('elena-cosmetics'),
    summary: {
      en: 'A bilingual beauty store built for shopping on the phone, with an AI beauty assistant on hand.',
      ar: 'متجر تجميل ثنائي اللغة مصمَّم للتسوّق من الهاتف، مع مساعد تجميل بالذكاء الاصطناعي.',
    },
    problem: {
      en: 'An Egyptian beauty brand selling lotions, glosses and highlighters to customers who shop in Arabic and English, many of them on mobile.',
      ar: 'علامة تجميل مصرية تبيع اللوشن وملمّع الشفاه والهايلايتر لعملاء يتسوّقون بالعربية والإنجليزية، وكثير منهم من الجوال.',
    },
    approach: {
      en: 'We designed an elegant, mobile-first store in both languages, with bestsellers up front, campaign sections for each hero product, bundles and reviews. An AI chat agent trained on Elena’s products answers shade and ingredient questions instantly, and every page is optimised so the brand shows up when people search.',
      ar: 'صمّمنا متجراً أنيقاً للجوال أولاً وباللغتين، يضع الأكثر مبيعاً في الواجهة، مع أقسام حملات لكل منتج رئيسي وباقات وتقييمات. ويجيب وكيل محادثة بالذكاء الاصطناعي مدرَّب على منتجات Elena عن أسئلة الدرجات والمكوّنات فوراً، وكل صفحة محسّنة لتظهر العلامة عندما يبحث الناس.',
    },
    result: {
      en: 'A store that reflects the brand and is easy to shop in either language.',
      ar: 'متجر يعكس العلامة ويسهل التسوّق فيه بأي من اللغتين.',
    },
    highlights: [
      included.bilingual,
      included.aiChat,
      { en: 'Bestsellers, bundles and campaign sections for hero products', ar: 'الأكثر مبيعاً والباقات وأقسام حملات للمنتجات الرئيسية' },
      included.reviews,
      included.seo,
      included.mobile,
      included.tracking,
      included.dashboard,
    ],
  },
  {
    slug: 'second-chance',
    name: 'Second Chance',
    url: 'secondchanceg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Women’s fashion', ar: 'أزياء نسائية' },
    thumbnail: '/images/secondchance.jpg',
    capture: null,
    summary: {
      en: 'A bold, confident store with a streetwear attitude.',
      ar: 'متجر جريء وواثق بروح أزياء الشارع.',
    },
    problem: {
      en: 'Second Chance sells jackets, hoodies and everyday wear with attitude, and the store needed the same confidence.',
      ar: 'تبيع Second Chance السترات والهوديز والملابس اليومية بروح جريئة، واحتاج المتجر إلى الثقة نفسها.',
    },
    approach: {
      en: 'We built a custom homepage led by lifestyle photography and hero banners, with collections organised by type and colour so shoppers find their piece fast. Reviews add trust, a floating WhatsApp button keeps the brand one tap away, and every page is optimised for search.',
      ar: 'بنينا صفحة رئيسية مخصّصة تقودها صور أسلوب الحياة واللافتات الكبيرة، مع مجموعات مرتّبة حسب النوع واللون ليجد المتسوّقون قطعتهم بسرعة. وتضيف التقييمات الثقة، ويُبقي زر واتساب العائم العلامة على بُعد لمسة، وكل صفحة محسّنة لمحركات البحث.',
    },
    result: {
      en: 'Quick product discovery and checkout on mobile, in the brand’s own voice.',
      ar: 'اكتشاف سريع للمنتجات ودفع سهل من الجوال، بصوت العلامة نفسها.',
    },
    highlights: [
      { en: 'Custom homepage with lifestyle hero banners', ar: 'صفحة رئيسية مخصّصة بصور أسلوب الحياة' },
      { en: 'Collections by type and colour', ar: 'مجموعات حسب النوع واللون' },
      included.whatsapp,
      included.reviews,
      included.seo,
      included.tracking,
      included.mobile,
      included.scalable,
    ],
  },
  {
    slug: 'toji',
    name: 'Toji',
    url: 'tojieg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Streetwear', ar: 'أزياء الشارع' },
    thumbnail: '/images/toji-thumb.jpg',
    capture: null,
    summary: {
      en: 'Raw visuals and big type for oversized Egyptian streetwear.',
      ar: 'صور خام وخطوط كبيرة لأزياء شارع مصرية واسعة القصّة.',
    },
    problem: {
      en: 'Toji’s oversized tees and sweatpants are loud and street-led. A standard template would have flattened that.',
      ar: 'قمصان Toji وبناطيلها الواسعة جريئة ونابعة من الشارع، وكان القالب العادي سيُفقدها هذه الروح.',
    },
    approach: {
      en: 'We designed a custom, product-first layout for oversized fits, with bold visuals and impactful typography that match the brand’s energy. Reviews add social proof, a floating WhatsApp button handles sizing questions, and every page is optimised for search.',
      ar: 'صمّمنا تصميماً مخصّصاً يضع المنتج أولاً للقصّات الواسعة، بصور جريئة وخطوط قوية تواكب طاقة العلامة. وتضيف التقييمات دليلاً اجتماعياً، ويتولّى زر واتساب العائم أسئلة المقاسات، وكل صفحة محسّنة لمحركات البحث.',
    },
    result: {
      en: 'A store that speaks directly to its audience.',
      ar: 'متجر يخاطب جمهوره مباشرة.',
    },
    highlights: [
      { en: 'Custom layout for oversized streetwear', ar: 'تصميم مخصّص لأزياء الشارع الواسعة' },
      { en: 'Bold visuals and typography', ar: 'صور وخطوط جريئة' },
      included.whatsapp,
      included.reviews,
      included.seo,
      included.tracking,
      included.mobile,
    ],
  },
  {
    slug: 'adaan',
    name: 'Adaan',
    url: 'adaaneg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Women’s fashion', ar: 'أزياء نسائية' },
    thumbnail: '/images/adaan-thumb.jpg',
    capture: capture('adaan'),
    summary: {
      en: 'A feminine, confident store with an AI assistant, built to turn browsing into orders.',
      ar: 'متجر أنثوي واثق مع مساعد بالذكاء الاصطناعي، مصمَّم لتحويل التصفّح إلى طلبات.',
    },
    problem: {
      en: 'Adaan sells bodysuits, sets and basics, and needed shoppers to find the right piece fast and feel confident ordering it.',
      ar: 'تبيع Adaan البودي والأطقم والقطع الأساسية، واحتاجت أن يجد المتسوّقون القطعة المناسبة بسرعة ويطلبوها بثقة.',
    },
    approach: {
      en: 'We organised the range into clear collections with filters, wrote keyword-led product titles so each piece can be found on Google, and kept the layout fast on every screen. An AI chat agent trained on Adaan’s catalogue answers fit and delivery questions the moment they come up.',
      ar: 'نظّمنا التشكيلة في مجموعات واضحة مع فلاتر، وكتبنا عناوين منتجات مبنية على الكلمات المفتاحية ليمكن إيجاد كل قطعة على جوجل، وحافظنا على سرعة التصميم على كل الشاشات. ويجيب وكيل محادثة بالذكاء الاصطناعي مدرَّب على منتجات Adaan عن أسئلة المقاس والتوصيل فور ظهورها.',
    },
    result: {
      en: 'A store that reflects a feminine yet confident identity, ready for upsells and loyalty.',
      ar: 'متجر يعكس هوية أنثوية واثقة، وجاهز للبيع الإضافي وبرامج الولاء.',
    },
    highlights: [
      { en: 'Collections and filters for quick discovery', ar: 'مجموعات وفلاتر لاكتشاف سريع' },
      { en: 'Keyword-led product titles that rank', ar: 'عناوين منتجات مبنية على الكلمات المفتاحية لتتصدّر البحث' },
      included.aiChat,
      included.seo,
      included.mobile,
      included.tracking,
      included.dashboard,
      included.scalable,
    ],
  },
  {
    slug: 'pura',
    name: 'Pura',
    url: 'puraeg.com',
    delivered: null,
    services: ['shopify'],
    industry: { en: 'Women’s fashion', ar: 'أزياء نسائية' },
    thumbnail: '/images/pura-thumb.jpg',
    capture: capture('pura'),
    summary: {
      en: 'A light, minimal store for calm, modern clothing.',
      ar: 'متجر خفيف وبسيط لملابس عصرية هادئة.',
    },
    problem: {
      en: 'Pura’s clothes are minimal and calm. The store needed to stay out of the way and let the products sell.',
      ar: 'ملابس Pura بسيطة وهادئة، وكان على المتجر ألا يشتّت الانتباه ويترك المنتجات تبيع.',
    },
    approach: {
      en: 'We built a lightweight, mobile-first store focused on clean UX and speed, with categories up front and the full range a scroll away. Every page is optimised for search, and a floating WhatsApp button lets customers reach the brand in one tap.',
      ar: 'بنينا متجراً خفيفاً للجوال أولاً يركّز على تجربة استخدام نظيفة وسريعة، مع الفئات في الواجهة والتشكيلة كاملة على بُعد تمرير. كل صفحة محسّنة لمحركات البحث، ويتيح زر واتساب العائم للعملاء الوصول إلى العلامة بلمسة واحدة.',
    },
    result: {
      en: 'A clean, product-focused store that’s quick to browse and easy to buy from.',
      ar: 'متجر نظيف يركّز على المنتج، سريع التصفّح وسهل الشراء.',
    },
    highlights: [
      { en: 'Lightweight layout built for speed', ar: 'تصميم خفيف مبني للسرعة' },
      { en: 'Categories up front for quick browsing', ar: 'الفئات في الواجهة لتصفّح سريع' },
      included.whatsapp,
      included.seo,
      included.tracking,
      included.mobile,
      included.scalable,
    ],
  },
];

export const projectBySlug = (slug) => projects.find((p) => p.slug === slug);
